/**
 * WGSL source generation for the six degree-of-freedom AVBD body solve.
 *
 * The emitted shader deliberately uses 21 named lower-triangle scalars.  The
 * TypeScript loops below only generate source text; the resulting WGSL has no
 * matrix arrays, dynamic indexing, pointer helpers, or factorization loops.
 * This mirrors the fully unrolled 6x6 LDL solve in the public AVBD 3D
 * reference while retaining our relative modified-pivot and equilibration
 * safeguards.
 */

export const PACKED_LDL6_DOF = 6;
export const AVBD_RELATIVE_LDL_PIVOT = 32.0 * 1.1920928955078125e-7;

const lowerName = (row: number, column: number): string => {
  if (row < column) return `a${column}${row}`;
  return `a${row}${column}`;
};

const lName = (row: number, column: number): string => `l${row}${column}`;

const indentLines = (source: string, indent: string): string => source
  .split('\n')
  .map((line) => line.length > 0 ? `${indent}${line}` : line)
  .join('\n');

export type PackedLdl6RankOneStampOptions = {
  row: readonly [string, string, string, string, string, string];
  stiffness: string;
  force: string;
  indent?: string;
};

/** Emit b += J^T f and lower(A) += k J^T J with scalar WGSL statements. */
export function makePackedLdl6RankOneStampWGSL(
  options: PackedLdl6RankOneStampOptions,
): string {
  const { row, stiffness, force, indent = '' } = options;
  const lines: string[] = [];
  for (let r = 0; r < PACKED_LDL6_DOF; r += 1) {
    lines.push(`b${r} += (${row[r]}) * (${force});`);
    for (let c = 0; c <= r; c += 1) {
      // Match the dense hot path's left-to-right f32 product:
      // stiffness * J[row] * J[column].
      lines.push(
        `${lowerName(r, c)} += (${stiffness}) * (${row[r]}) * (${row[c]});`,
      );
    }
  }
  return indentLines(lines.join('\n'), indent);
}

export type PackedModifiedLdl6SolveOptions = {
  relativePivot: string;
  indent?: string;
  modifiedPivotCountVariable?: string;
};

/**
 * Emit a complete, scalar, diagonally-equilibrated relative modified LDL^T
 * solve.  Inputs are mutable a00,a10,a11,...,a55 and b0,...,b5 variables.
 * Outputs are x0,...,x5 and solveOutputFinite.  The caller supplies an
 * isFiniteF32(f32)->bool helper, as the production shader already does.
 */
export function makePackedModifiedLdl6SolveWGSL(
  options: PackedModifiedLdl6SolveOptions,
): string {
  const {
    relativePivot,
    indent = '',
    modifiedPivotCountVariable,
  } = options;
  const lines: string[] = [];

  lines.push('var solveInputFinite = true;');

  // The dense implementation stores both triangles and explicitly averages
  // them. Repeat 0.5*(a+a) to retain that sanitization/overflow boundary for a
  // symmetric packed input. Production rank-one stamping keeps the dense
  // lower-product order; it is algebraically, but not bitwise, equivalent to
  // separately accumulating the upper product and averaging both triangles.
  for (let r = 0; r < PACKED_LDL6_DOF; r += 1) {
    for (let c = r + 1; c < PACKED_LDL6_DOF; c += 1) {
      const a = lowerName(c, r);
      const raw = `sRaw${c}${r}`;
      lines.push(`let ${raw} = 0.5 * (${a} + ${a});`);
      lines.push(`if (!isFiniteF32(${raw})) { solveInputFinite = false; }`);
      lines.push(`${a} = select(0.0, ${raw}, isFiniteF32(${raw}));`);
    }

    const a = lowerName(r, r);
    lines.push(`let diagonalRaw${r} = ${a};`);
    lines.push(
      `if (!isFiniteF32(diagonalRaw${r}) || !isFiniteF32(b${r})) { solveInputFinite = false; }`,
    );
    lines.push(`${a} = select(0.0, diagonalRaw${r}, isFiniteF32(diagonalRaw${r}));`);
    lines.push(`b${r} = select(0.0, b${r}, isFiniteF32(b${r}));`);
    lines.push(`let equil${r} = inverseSqrt(max(abs(${a}), 1e-30));`);
  }

  for (let r = 0; r < PACKED_LDL6_DOF; r += 1) {
    lines.push(`let scaledB${r} = b${r} * equil${r};`);
    lines.push(`if (!isFiniteF32(scaledB${r})) { solveInputFinite = false; }`);
    lines.push(`b${r} = select(0.0, scaledB${r}, isFiniteF32(scaledB${r}));`);
    for (let c = 0; c <= r; c += 1) {
      const a = lowerName(r, c);
      lines.push(`let scaledA${r}${c} = ${a} * equil${r} * equil${c};`);
      lines.push(`if (!isFiniteF32(scaledA${r}${c})) { solveInputFinite = false; }`);
      lines.push(`${a} = select(0.0, scaledA${r}${c}, isFiniteF32(scaledA${r}${c}));`);
    }
  }

  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    lines.push(
      `let pivotFloor${i} = max(abs(${lowerName(i, i)}) * (${relativePivot}), (${relativePivot}));`,
    );
  }
  if (modifiedPivotCountVariable) {
    lines.push(`var ${modifiedPivotCountVariable}: u32 = 0u;`);
  }

  // Row-oriented LDL^T, matching the operation order of the former WGSL
  // loops.  This differs slightly from algebraically regrouped textbook code
  // and is intentional for f32 equivalence.
  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    for (let j = 0; j < i; j += 1) {
      const sum = `sumL${i}${j}`;
      lines.push(`var ${sum} = ${lowerName(i, j)};`);
      for (let k = 0; k < j; k += 1) {
        lines.push(`${sum} -= ${lName(i, k)} * d${k} * ${lName(j, k)};`);
      }
      lines.push(`if (!isFiniteF32(${sum})) { solveInputFinite = false; }`);
      lines.push(`let finiteSumL${i}${j} = select(0.0, ${sum}, isFiniteF32(${sum}));`);
      lines.push(`let rawL${i}${j} = finiteSumL${i}${j} / d${j};`);
      lines.push(`if (!isFiniteF32(rawL${i}${j})) { solveInputFinite = false; }`);
      lines.push(`let ${lName(i, j)} = select(0.0, rawL${i}${j}, isFiniteF32(rawL${i}${j}));`);
    }

    lines.push(`var diag${i} = ${lowerName(i, i)};`);
    for (let k = 0; k < i; k += 1) {
      lines.push(`let diagContribution${i}${k} = ${lName(i, k)} * ${lName(i, k)} * d${k};`);
      lines.push(
        `if (!isFiniteF32(diagContribution${i}${k})) { solveInputFinite = false; }`,
      );
      lines.push(
        `diag${i} -= select(0.0, diagContribution${i}${k}, isFiniteF32(diagContribution${i}${k}));`,
      );
    }
    lines.push(`let healthyPivot${i} = isFiniteF32(diag${i}) && diag${i} >= pivotFloor${i};`);
    lines.push(`let d${i} = select(pivotFloor${i}, diag${i}, healthyPivot${i});`);
    if (modifiedPivotCountVariable) {
      lines.push(
        `${modifiedPivotCountVariable} += select(1u, 0u, healthyPivot${i});`,
      );
    }
  }

  // Ly=b.  Keep every subtraction as its own statement to match the old loop.
  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    lines.push(`var y${i} = b${i};`);
    for (let k = 0; k < i; k += 1) {
      lines.push(`y${i} -= ${lName(i, k)} * y${k};`);
    }
  }

  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    lines.push(`let z${i} = y${i} / d${i};`);
  }

  // L^T x=z, descending.
  for (let row = PACKED_LDL6_DOF - 1; row >= 0; row -= 1) {
    lines.push(`var x${row} = z${row};`);
    for (let c = row + 1; c < PACKED_LDL6_DOF; c += 1) {
      lines.push(`x${row} -= ${lName(c, row)} * x${c};`);
    }
  }

  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    lines.push(`x${i} *= equil${i};`);
  }
  lines.push('var solveOutputFinite = solveInputFinite;');
  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    lines.push(`solveOutputFinite = solveOutputFinite && isFiniteF32(x${i});`);
  }
  lines.push('if (!solveOutputFinite) {');
  for (let i = 0; i < PACKED_LDL6_DOF; i += 1) {
    lines.push(`  x${i} = 0.0;`);
  }
  lines.push('}');

  return indentLines(lines.join('\n'), indent);
}
